import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Briefcase, MapPin, Clock, Building2, ExternalLink, 
  Plus, Loader2, CheckCircle, XCircle, X, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const jobTypes = ["full-time", "part-time", "contract", "freelance", "internship"];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedJob, setSelectedJob] = useState(null);
  const [emptyContent, setEmptyContent] = useState({
    headline: "The Industry is Hiring",
    message: "The Job Board connects organizations with the next generation of filmmakers. Listings are updated on a rolling basis as productions greenlight new roles."
  });
  const [form, setForm] = useState({
    title: "",
    company: "",
    location: "",
    job_type: "full-time",
    description: "",
    requirements: "",
    salary_range: "",
    application_url: "",
    contact_email: ""
  });

  useEffect(() => {
    fetchJobs();
    fetchMyJobs();
    fetchEmptyContent();
  }, [selectedType]);

  const fetchEmptyContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content/jobs/empty_state`);
      if (response.data.content && Object.keys(response.data.content).length > 0) {
        setEmptyContent(prev => ({ ...prev, ...response.data.content }));
      }
    } catch (error) {
      // Use defaults
    }
  };

  const fetchJobs = async () => {
    try {
      const url = selectedType === "all" 
        ? `${API}/jobs` 
        : `${API}/jobs?job_type=${selectedType}`;
      const response = await axios.get(url);
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs/my`, { withCredentials: true });
      setMyJobs(response.data);
    } catch (error) {
      // User might not be logged in
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company || !form.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Require either application URL or contact email
    if (!form.application_url && !form.contact_email) {
      toast.error("Please provide either an application URL or contact email");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs`, form, { withCredentials: true });
      toast.success("Job posted! It will be visible after admin approval.");
      setForm({
        title: "", company: "", location: "", job_type: "full-time",
        description: "", requirements: "", salary_range: "", 
        application_url: "", contact_email: ""
      });
      setShowForm(false);
      fetchMyJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post job");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected": return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple mb-4 font-mono">
            Opportunities
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            Job Board
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Find opportunities in AI filmmaking or post jobs for your team. 
            All job postings are reviewed by our team before going live.
          </p>
        </motion.div>

        {/* Post Job Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Button
            onClick={() => setShowForm(!showForm)}
            data-testid="post-job-btn"
            className="bg-purple hover:bg-purple/80 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? "Cancel" : "Post a Job"}
          </Button>
        </motion.div>

        {/* Job Posting Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-12"
          >
            <div className="p-6 bg-void-surface border border-white/5">
              <h2 className="font-syne font-bold text-white text-xl mb-6">Post a New Job</h2>
              <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Job Title *</label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., AI Visual Effects Artist"
                    data-testid="job-title"
                    className="bg-void border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Company *</label>
                  <Input
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Your company name"
                    data-testid="job-company"
                    className="bg-void border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Location</label>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g., Remote, New York, etc."
                    data-testid="job-location"
                    className="bg-void border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Job Type</label>
                  <select
                    value={form.job_type}
                    onChange={(e) => setForm({ ...form, job_type: e.target.value })}
                    data-testid="job-type"
                    className="w-full px-3 py-2 bg-void border border-white/10 text-white rounded-md"
                  >
                    {jobTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Salary Range</label>
                  <Input
                    value={form.salary_range}
                    onChange={(e) => setForm({ ...form, salary_range: e.target.value })}
                    placeholder="e.g., $80k - $120k"
                    data-testid="job-salary"
                    className="bg-void border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Application URL *</label>
                  <Input
                    value={form.application_url}
                    onChange={(e) => setForm({ ...form, application_url: e.target.value })}
                    placeholder="Link to apply"
                    data-testid="job-apply-url"
                    className="bg-void border-white/10 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Required if no contact email</p>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Contact Email *</label>
                  <Input
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="Email for applications"
                    data-testid="job-contact-email"
                    className="bg-void border-white/10 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Required if no application URL</p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-400 mb-2">Description *</label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Describe the role, responsibilities, and what you're looking for"
                    data-testid="job-description"
                    className="bg-void border-white/10 text-white min-h-[120px]"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-400 mb-2">Requirements</label>
                  <Textarea
                    value={form.requirements}
                    onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                    placeholder="List the required skills and qualifications"
                    data-testid="job-requirements"
                    className="bg-void border-white/10 text-white min-h-[100px]"
                  />
                </div>

                <div className="md:col-span-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-job-btn"
                    className="bg-purple hover:bg-purple/80 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      "Post Job"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters */}
          <div className="lg:col-span-1">
            <div className="p-6 bg-void-surface border border-white/5 sticky top-24">
              <h3 className="font-syne font-bold text-white mb-4">Filter by Type</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedType("all")}
                  data-testid="job-filter-all"
                  className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                    selectedType === "all"
                      ? "bg-purple/20 text-purple-light"
                      : "text-slate-500 hover:text-white hover:bg-white/5"
                  }`}
                >
                  All Jobs
                </button>
                {jobTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    data-testid={`job-filter-${type}`}
                    className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                      selectedType === type
                        ? "bg-purple/20 text-purple-light"
                        : "text-slate-500 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>

              {/* My Job Postings */}
              {myJobs.length > 0 && (
                <div className="mt-8 pt-6 border-t border-white/5">
                  <h3 className="font-syne font-bold text-white mb-4">My Postings</h3>
                  <div className="space-y-2">
                    {myJobs.map((job) => (
                      <div key={job.id} className="flex items-center gap-2 text-sm">
                        {getStatusIcon(job.status)}
                        <span className="text-slate-400 truncate">{job.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Jobs List */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-purple animate-spin" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-20">
                <Briefcase className="w-16 h-16 text-purple/30 mx-auto mb-6" />
                <h2 className="font-syne text-2xl font-bold text-white mb-4">{emptyContent.headline}</h2>
                <p className="text-slate-400 max-w-md mx-auto">
                  {emptyContent.message}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`job-${index}`}
                    onClick={() => setSelectedJob(job)}
                    className="p-6 bg-void-surface border border-white/5 hover:border-purple/20 transition-all duration-500 cursor-pointer"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-grow">
                        <span className="inline-block px-2 py-1 text-xs uppercase tracking-wider bg-purple/20 text-purple-light mb-3">
                          {job.job_type}
                        </span>
                        <h3 className="font-syne font-bold text-white text-xl mb-1">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {job.company}
                          </span>
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                          )}
                          {job.salary_range && (
                            <span className="text-green-500">
                              {job.salary_range}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-400 mb-4 line-clamp-2">
                          {job.description}
                        </p>
                        <span className="text-sm text-purple-light">Click to view details →</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Job Detail Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setSelectedJob(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-void-surface border border-white/10 p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="inline-block px-2 py-1 text-xs uppercase tracking-wider bg-purple/20 text-purple-light mb-3">
                    {selectedJob.job_type}
                  </span>
                  <h2 className="font-syne font-bold text-white text-2xl">
                    {selectedJob.title}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-6 pb-6 border-b border-white/10">
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {selectedJob.company}
                </span>
                {selectedJob.location && (
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedJob.location}
                  </span>
                )}
                {selectedJob.salary_range && (
                  <span className="text-green-500 font-medium">
                    {selectedJob.salary_range}
                  </span>
                )}
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-400 mb-3">Description</h3>
                <p className="text-white whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              {selectedJob.requirements && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Requirements</h3>
                  <p className="text-white whitespace-pre-wrap">{selectedJob.requirements}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-4 pt-6 border-t border-white/10">
                {selectedJob.application_url && (
                  <a
                    href={selectedJob.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs hover:bg-purple-hover transition-all"
                  >
                    Apply Now <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {selectedJob.contact_email && (
                  <a
                    href={`mailto:${selectedJob.contact_email}?subject=Application for ${selectedJob.title}`}
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-bold uppercase tracking-widest text-xs hover:border-purple/50 transition-all"
                  >
                    <Mail className="w-4 h-4" />
                    Contact via Email
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
